import type { Scene } from '@seddi/three';

import { degreeToRadian } from '../core/units';
import type { ResourcesContext } from '../resources/resourcesContext';
import {
  type EntityRenderable,
  create as createEntityInternal,
  dispose as disposeEntityInternal,
} from './entityRenderable';

/**
 * Scene renderable.
 */
export interface SceneRenderable {
  readonly entities: Map<string, EntityRenderable>;
  environmentRef?: string;
  readonly scene: Scene;
}

/**
 * Create renderable.
 * @param scene - GFX scene.
 */
export function create(scene: Scene): SceneRenderable {
  return { entities: new Map(), scene };
}

/**
 * Create entity.
 * @param self - Renderable.
 * @param id - Entity ID.
 */
export function createEntity(
  self: SceneRenderable,
  id: string,
  parent?: EntityRenderable,
): EntityRenderable {
  const entity = createEntityInternal(id, parent);
  self.entities.set(id, entity);
  self.scene.add(entity.node);
  return entity;
}

/**
 * Dispose entity.
 * @param self - Renderable.
 * @param id - Entity ID.
 * @param resources - Resources context.
 */
export function disposeEntity(self: SceneRenderable, id: string, resources: ResourcesContext) {
  const entity = self.entities.get(id);
  disposeEntityInternal(entity, resources, (entity) => {
    self.entities.delete(entity.id);
  });
}

/**
 * Get entity.
 * @param self - Renderable.
 * @param id - Entity ID.
 */
export function getEntity(self: SceneRenderable, id: string): EntityRenderable | undefined {
  return self.entities.get(id);
}

/**
 * Attach environment.
 * @param self - Renderable.
 * @param id - Environment ID.
 * @param resources - Resources context.
 */
export function attachEnvironment(self: SceneRenderable, id: string, resources: ResourcesContext) {
  // Capture resource ref.
  const resource = resources.getRefEnvironment(id)!;
  self.environmentRef = resource.id;
  // Set scene.
  // TODO: This could play bad with scene state environment rotation/same for exposure
  const rotation = degreeToRadian(resource.rotation ?? 0);
  const { scene } = self;
  scene.environment = resource.texture;
  scene.environmentRotation.set(0, rotation, 0);
}

/**
 * Detach environment.
 * @param self - Renderable.
 * @param context - Resources context.
 */
export function detachEnvironment(self: SceneRenderable, context: ResourcesContext) {
  if (!self.environmentRef) return;
  // Reset resource ref.
  context.retRefEnvironment(self.environmentRef);
  delete self.environmentRef;
  const { scene } = self;
  // Reset scene.
  scene.environment = null;
  scene.environmentRotation.set(0, 0, 0);
}
